/**
 * 線区 (route_id) と方面 (direction_name) のドロップダウンセレクタ。
 *
 * - 一覧画面上部に常時表示される唯一の入力 UI。線区を選ぶと方面の候補が動的に切り替わる。
 * - 「(全線区)」を選ぶと絞り込まずに全線区の放送地点を表示できる。
 *   このとき `onChange` には {@link ALL_ROUTES} (`'__all__'`) を渡す。
 *   空文字は「未選択 = トップページの線区選択ランディング」の意味で使われているため、
 *   「(全線区)」に空文字を割り当てていると選んだ瞬間にランディング画面へ戻ってしまう
 *   (実際に発生した不具合)。そのため専用のセンチネル値で区別している。
 *
 * 変更履歴 / 設計意図:
 *   - 以前はドロップダウンの左隣に「選択中の線区/方面バッジ」も出していたが、
 *     ドロップダウンの現在値と同じ情報 (例: 「京浜東北・根岸線 / 南行」) が
 *     2 箇所に並んで冗長だった。そこで **バッジを廃止** し、選択中の線区/方面は
 *     ドロップダウンの表示だけに一本化した。
 *   - 代わりに線区カラー (`routes-master.json` の `route_color` /
 *     `route_text_color`) を **操作パネル全体を囲む枠** として表現する。
 *   - 当初は「パネル左端のアクセントライン」+「ドロップダウン下の太いライン」という
 *     細いライン 2 本だったが、着色量が少なく「今どの線区を編集中か」が
 *     分かりにくいという指摘を受けて、マーカーで囲んだような枠表現に変更した
 *     (細いライン 2 本は廃止)。
 *     ※ 黄色系の線区もあるため、塗りはごく薄くして枠線主体にし、可読性を保つ
 *       (具体的な見せ方は styles.css の `.route-selector--colored` に閉じ込める)。
 *       文字色が必要な箇所ではマスタの `route_text_color` を尊重する。
 */
import { useMemo } from 'react';

import { getRouteColor, type RouteMaster } from '../data/routes';
import { ALL_ROUTES } from '../hooks/useRouteSelection';

interface RouteSelectorProps {
  routes: RouteMaster[];
  /**
   * 現在選択中の route_id。
   * 空文字 = 未選択 / {@link ALL_ROUTES} = 全線区 (線区で絞り込まない)
   */
  selectedRouteId: string;
  /** 現在選択中の direction_name (空文字 = 未選択) */
  selectedDirectionName: string;
  onChange: (routeId: string, directionName: string) => void;
  /** 新規作成ボタンが押されたときに呼び出される */
  onCreate?: () => void;
  /** 再読み込みボタン */
  onReload?: () => void;
  /** 一括 Import / Export 等の追加アクション (任意) */
  extraActions?: React.ReactNode;
  /**
   * トップページ (線区選択画面) に戻るハンドラ (任意)。
   * 指定時のみ「← 線区選択」ボタンを先頭に表示する。
   */
  onBackToTop?: () => void;
}

export function RouteSelector({
  routes,
  selectedRouteId,
  selectedDirectionName,
  onChange,
  onCreate,
  onReload,
  extraActions,
  onBackToTop,
}: RouteSelectorProps): React.JSX.Element {
  const selectedRoute = useMemo(
    () => routes.find((r) => r.route_id === selectedRouteId),
    [routes, selectedRouteId],
  );

  const directions = selectedRoute?.directions ?? [];

  /**
   * 単一線区が確定しているか。
   * 全線区 (ALL_ROUTES) / 未選択 (空文字) のときは false になり、
   * 方面セレクトを無効化する (絞り込める方面が特定できないため)。
   */
  const hasSingleRoute = Boolean(selectedRoute);

  /**
   * セレクトに表示する値。
   * 未選択 (空文字) の場合は該当する `<option>` が無いため、
   * 「(全線区)」を表示値として使う (実運用ではこのコンポーネントは
   *  線区選択後にのみ描画されるが、React の value 未一致警告を避ける)。
   */
  const routeSelectValue = selectedRouteId || ALL_ROUTES;

  /**
   * 選択中の線区カラー。未選択・全線区のときは null で、囲み枠を描画しない。
   * (`getRouteColor` は未定義の route_id に対してフォールバック色を返すため、
   *  「未選択」判定は selectedRoute の有無で行う)
   */
  const routeColor = selectedRoute ? getRouteColor(routes, selectedRoute.route_id) : null;

  return (
    <div
      className={`route-selector${routeColor ? ' route-selector--colored' : ''}`}
      data-testid="route-selector"
      // 線区カラーは CSS 変数として渡し、実際の見せ方 (枠の太さ・塗りの濃さ等) は
      // styles.css 側に閉じ込める。
      style={
        routeColor
          ? ({
              '--route-color': routeColor.background,
              '--route-text-color': routeColor.text,
            } as React.CSSProperties)
          : undefined
      }
    >
      {onBackToTop ? (
        <button
          type="button"
          className="route-selector__back"
          data-testid="route-selector-back"
          onClick={onBackToTop}
          title="線区選択画面に戻る"
        >
          ← 線区選択
        </button>
      ) : null}
      {/*
        線区 / 方面セレクタ。
        選択中の線区カラーは、このグループ単体ではなく操作パネル全体を囲む枠
        (`.route-selector--colored`) で示す。以前ここに引いていた
        「下端の太いライン」は枠と二重表現になるため廃止した。
        また「選択中の線区/方面バッジ」も、ここの表示と内容がダブるため廃止済み。
      */}
      <div className="route-selector__selects" data-testid="route-selector-selects">
        <label>
          <span>線区</span>
          <select
            data-testid="route-select"
            aria-label="線区"
            // 幅上限で省略表示されても全文を確認できるようにする
            title={
              selectedRoute ? `${selectedRoute.route_name} (${selectedRoute.route_id})` : undefined
            }
            value={routeSelectValue}
            onChange={(e) => {
              const newRouteId = e.target.value;
              // 線区変更時は方面をリセットする (互換性のため空文字で再選択を促す)
              onChange(newRouteId, '');
            }}
          >
            {/*
              「(全線区)」は空文字ではなく ALL_ROUTES を値に使う。
              空文字だと「未選択」と区別できず、選択時にトップページ
              (線区選択ランディング) へ戻ってしまうため。
            */}
            <option value={ALL_ROUTES}>(全線区)</option>
            {routes.map((r) => (
              <option key={r.route_id} value={r.route_id}>
                {r.route_name} ({r.route_id})
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>方面</span>
          <select
            data-testid="direction-select"
            aria-label="方面"
            title={selectedDirectionName || undefined}
            value={selectedDirectionName}
            onChange={(e) => onChange(selectedRouteId, e.target.value)}
            // 未選択 / 全線区のときは方面を特定できないので選べない
            disabled={!hasSingleRoute}
          >
            <option value="">(全方面)</option>
            {directions.map((d) => (
              <option key={d.direction_name} value={d.direction_name}>
                {d.direction_name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {onCreate ? (
        <button
          type="button"
          data-testid="create-button"
          className="primary create-button"
          onClick={onCreate}
          // 全線区モードでは登録先の方面が決まらないため作成できない
          disabled={!hasSingleRoute || !selectedDirectionName}
          title={
            !hasSingleRoute || !selectedDirectionName
              ? '新規作成するには線区と方面を選択してください'
              : ''
          }
        >
          ＋ 新規作成
        </button>
      ) : null}
      <div className="actions">
        {extraActions}
        {onReload ? (
          <button type="button" data-testid="reload-button" onClick={onReload}>
            再読み込み
          </button>
        ) : null}
      </div>
    </div>
  );
}
